import { type ReactElement, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { formatDate } from '../../shared/format.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Button, ErrorAlert, LoadingBlock, PageHeader } from '../../shared/ui/index.js';
import { DayByDayRecap } from './DayByDayRecap.js';
import { EditSojournDatesDialog } from './EditSojournDatesDialog.js';
import { PlanLocks } from './PlanLocks.js';
import { SojournGuests } from './SojournGuests.js';
import { SojournHouses } from './SojournHouses.js';
import { SojournItinerary } from './SojournItinerary.js';
import { useSojourn } from './sojournHooks.js';

export function SojournDetailPage(): ReactElement {
  const t = useTranslation();
  const { sojournId = '' } = useParams();
  const sojourn = useSojourn(sojournId);
  const [editingDates, setEditingDates] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);

  if (sojourn.isPending) {
    return <LoadingBlock />;
  }

  if (sojourn.error !== null || sojourn.data === undefined) {
    return (
      <div className="stack">
        <ErrorAlert error={sojourn.error} />
        <Link to="/sojourns">{t.sojournDetail.backToSojourns}</Link>
      </div>
    );
  }

  const ready = sojourn.data.houses.length > 0 && sojourn.data.guests.length > 0;

  return (
    <div className="stack stack--loose">
      <PageHeader
        title={sojourn.data.name}
        subtitle={
          <>
            {formatDate(sojourn.data.startDate)} → {formatDate(sojourn.data.endDate)} ·{' '}
            {t.count.nights(sojourn.data.nights)} · {t.count.houses(sojourn.data.houseCount)} ·{' '}
            {t.count.guests(sojourn.data.guestCount)}
          </>
        }
        actions={
          <>
            <Button variant="secondary" onClick={() => setEditingDates(true)}>
              {t.sojournDetail.editDates}
            </Button>
            <Button
              variant="secondary"
              disabled={!sojourn.data.hasPlan}
              onClick={() => setRecapOpen(true)}
            >
              {t.sojournDetail.dayByDayRecap}
            </Button>
            <Link to={`/sojourns/${sojournId}/plan`}>
              <Button disabled={!ready}>
                {sojourn.data.hasPlan ? t.sojournsPage.viewThePlan : t.sojournsPage.makeAPlan}
              </Button>
            </Link>
          </>
        }
      />

      <SojournHouses sojourn={sojourn.data} />
      <SojournGuests sojourn={sojourn.data} />
      <SojournItinerary sojourn={sojourn.data} />
      <PlanLocks sojourn={sojourn.data} />

      {editingDates ? (
        <EditSojournDatesDialog sojourn={sojourn.data} onClose={() => setEditingDates(false)} />
      ) : null}
      {recapOpen ? (
        <DayByDayRecap sojourn={sojourn.data} onClose={() => setRecapOpen(false)} />
      ) : null}
    </div>
  );
}
